import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { ListEntry } from "./ListEntry";
import { useGameIdFromRoute } from "../WithGameIdFromRoute";
import { useReorderGameTask } from "@seabrookstudios/pitch2table";

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").Task[]} props.tasks
 */
export const List = ({ tasks }) => {
  const gameId = useGameIdFromRoute();
  const reorderTask = useReorderGameTask(gameId);
  const [optimisticTasks, setOptimisticTasks] = useState(tasks);
  const isReorderingRef = useRef(false);
  const currentOptimisticOrderRef = useRef(optimisticTasks.map((task) => task.id).join(","));

  // Sync local state with props when tasks change, but only if:
  // 1. Not currently reordering (mutation pending)
  // 2. The task order actually changed from our current optimistic state
  useEffect(() => {
    // Don't sync if we're in the middle of a reorder operation
    if (isReorderingRef.current || reorderTask.isPending) {
      return;
    }

    // Compare task order by IDs
    const newOrder = tasks.map((task) => task.id).join(",");
    const currentOptimisticOrder = currentOptimisticOrderRef.current;

    // Only sync if the order actually changed from our current optimistic state
    // or if tasks were added/removed
    if (newOrder !== currentOptimisticOrder || tasks.length !== optimisticTasks.length) {
      setTimeout(() => {
        setOptimisticTasks(tasks);
        currentOptimisticOrderRef.current = newOrder;
      }, 1000);
    }
  }, [tasks, reorderTask.isPending, optimisticTasks.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  if (optimisticTasks.length === 0) {
    return <NoTasks />;
  }

  /**
   * @param {import("@dnd-kit/core").DragEndEvent} event
   */
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = optimisticTasks.findIndex((task) => task.id === active.id);
      const newIndex = optimisticTasks.findIndex((task) => task.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        console.error("Task not found in tasks array", { activeId: active.id, overId: over.id });
        return;
      }

      // Optimistically update the UI immediately
      const reorderedTasks = arrayMove(optimisticTasks, oldIndex, newIndex);
      const taskOrder = reorderedTasks.map((task) => task.id);
      const newOrder = taskOrder.join(",");

      setOptimisticTasks(reorderedTasks);

      // Update the current optimistic order to reflect our optimistic update
      currentOptimisticOrderRef.current = newOrder;

      // Mark that we're reordering to prevent sync during mutation
      isReorderingRef.current = true;

      try {
        await reorderTask.mutateAsync({ taskOrder });
      } finally {
        isReorderingRef.current = false;
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={optimisticTasks.map((task) => task.id)} strategy={verticalListSortingStrategy}>
        <Box>
          {optimisticTasks.map((task, index) => (
            <ListEntry key={task.id} item={task} filteredIndex={index} />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export const NoTasks = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <Typography variant="body1" color="text.secondary">
        No tasks yet. Add your first task to get started!
      </Typography>
    </Box>
  );
};
